<template>
  <div class="section-card" :class="{'blog-card': type == 'blog'}">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive, getMarkAttrs }">
      <div class="menubar">

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <icon name="bold" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <icon name="italic" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
        >
          <icon name="strike" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >
          <icon name="underline" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >
          <icon name="paragraph" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >
          H1
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >
          H2
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >
          H3
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >
          <icon name="ul" />
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >
          <icon name="ol" />
        </button>

        <button
            v-if="!linkMenuIsActive"
            class="menububble__button"
            @click="showLinkMenu(getMarkAttrs('link'))"
            :class="{ 'is-active': isActive.link() }"
          >
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link'}}</span>
            <icon name="link" />
          </button>

        <form class="menububble__form" v-else @submit.prevent="setLinkUrl(commands.link, linkUrl)">
          <input class="menububble__input" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
          <button class="menububble__button" @click="setLinkUrl(commands.link, null)" type="button">
            <icon name="remove" />
          </button>
        </form>

      </div>
    </editor-menu-bar>      
    <editor-content v-model="bodyVal" :editor="editor" />

    <div class="buttons">
      <button class="secondary-btn-dark" @click="discardChanges">DISCARD</button>
      <button class="primary-btn" @click="saveClicked">SAVE CHANGES</button>
    </div>
  </div>
</template>

<script>
import { saveSection, createSection, deleteSection } from "@/api/requests";
import Icon from "@/components/content_editor/Icon";
import TocHeading from "@/components/content_editor/TocHeading.js";
import { Editor, EditorContent, EditorMenuBar  } from 'tiptap'
import {
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Italic,
  Strike,
  Underline,
  Image,
  Link
} from 'tiptap-extensions'

export default {
  name: 'TextEditor',

  components: {
    EditorContent,
    EditorMenuBar,
    Icon
  },

  props: {
    type: {
      type: String,
      required: true,
    },
    heading: {
      type: String,
      required: true
    },
    body: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    new: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      bodyVal: '',
      editor: null,
      linkUrl: null,
      linkMenuIsActive: false,
    }
  },

  mounted() {
    this.bodyVal = this.body;
    this.editor = new Editor({
      editorProps: {
        attributes: {
          class: 'pm-input-mode',
        },
      },
      editable: true,
      content: JSON.parse(this.bodyVal),
      extensions: [
          new BulletList(),
          new TocHeading({ levels: [1, 2, 3] }),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Bold(),
          new Italic(),
          new Strike(),
          new Underline(),
          new Image(),
          new Link(),
        ],
    })
  },

  methods: {
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
      command({ href: url, target: "null" })
      this.hideLinkMenu()
    },

    discardChanges() {
      this.bodyVal = this.body;
    },

    saveClicked() {
      if (true) {
        const jsonOutput = this.editor.getJSON();
        this.$emit('saveBlog', JSON.stringify(jsonOutput));
      } else {
        this.new ? this.createNew() : this.saveChanges();
      }
    },

    createNew() {
      const payload = {
        heading: this.headingVal,
        body: this.bodyVal,
        doc_type: this.type,
      };
      createSection(payload).then(() => {
        this.$store.commit('content/updateNewSection', payload);
        this.$toasted.global.app_success({
          message: "Content has been updated."
        });
      }).catch(() => {
        this.$toasted.global.app_error({
          message: "Failed to update content."
        });
      });
    },

    saveChanges() {
      const jsonOutput = this.editor.getJSON();
      const payload = {
        body: JSON.stringify(jsonOutput),
      };
      saveSection(this.id, payload).then(() => {
        this.$toasted.global.app_success({
          message: "Content has been updated."
        });
      }).catch(() => {
        this.$toasted.global.app_error({
          message: "Failed to update content."
        });
      });
    },

    deleteClicked() {
      this.new ? this.deleteNew() : this.deleteExisting();
    },

    deleteNew() {
      this.$store.commit('content/deleteNewSection');
      this.$toasted.global.app_success({
        message: "New section has been updated."
      });
    },

    deleteExisting() {
      this.$confirm(
        "Are you sure you want to delete this section?", 
        "DELETE SECTION").then(() => {
          deleteSection(this.id).then(() => {
            this.$store.commit('content/deleteSection', this.id);
            this.$toasted.global.app_success({
              message: "Section has been deleted."
            });
          });
        }).catch(() => {
          this.$toasted.global.app_error({
            message: "Failed to delete section."
          });
        });
    }
  }

}
</script>

<style lang="scss">
@import "@/assets/css/variables";
@import "@/assets/css/internal";

.section-card {
  max-width: 600px;
  width: 100%;
  background: $sec0;
  // margin: $spacing-base;
  display: flex;
  flex-direction: column;
  padding: $spacing-small;

  input {
    background: $dark;
    margin-bottom: $spacing-small;
  }

  textarea {
    background: $dark;
    margin-bottom: $spacing-small;
    resize: none;
  }

  label {
    font-size: $font-size-base;
    font-weight: 500;
    color: $sec6;
    margin-bottom: $spacing-small/2;
  }

  .buttons {
    margin-top: 0;
    width: 100%;
    display: flex;
    .primary-btn {
      flex: 2;
    }
    span {
      flex: 2;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      
      img {
        cursor: pointer;
      }
    }
  }
}

.blog-card {
  width: 100%;
  max-width: none;
}

.menubar__button {
  background: transparent;
  border: 0;
  color: #FFF;
  padding: 4px;

  &:hover {
    cursor: pointer;
    background: $sec2;
  }
}

.is-active {
  background: $sec1;
}

.pm-input-mode {
  outline: 0;
  color: #FFF;

  img {
    width: 200px;
  }
}
</style>